/**
 * Real-time playback of a composition onto a canvas.
 *
 * The preview, and the MediaRecorder export fallback, both run on this. It
 * keeps each clip's <video> (and each music <audio>) playing in step with the
 * composition clock and draws every animation frame with `renderFrame`.
 */
import {
    MainClip,
    VideoComposition,
    audioTracks,
    clamp01,
    clipSpeed,
    itemEnd,
    mainFrameState,
    mainTrack,
    sourceTimeAt,
    totalDuration,
} from './composition';
import { MediaCache } from './media';
import { LayerBox, renderFrame } from './render';

/** How far video may drift from the clock before it is re-seeked. */
const DRIFT = 0.2;
/** Clips about to start are pre-rolled this many seconds early. */
const PREROLL = 0.6;

export class PlaybackEngine {
    private comp: VideoComposition | null = null;
    private raf = 0;
    private renderQueued = 0;
    private wallStart = 0;
    private timeStart = 0;
    private unsub: () => void;
    time = 0;
    playing = false;
    /** Fired every frame while playing, and on seek. */
    onTime?: (t: number) => void;
    onPlayingChange?: (playing: boolean) => void;
    /** Fired after every drawn frame (the preview positions its selection box from hitBoxes). */
    onDraw?: () => void;
    /** When set, playback stops at the end instead of looping. */
    stopAtEnd = true;
    readonly hitBoxes = new Map<string, LayerBox>();
    private ctx: CanvasRenderingContext2D;

    constructor(private readonly canvas: HTMLCanvasElement, readonly media: MediaCache) {
        this.ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
        this.unsub = media.onChange(() => this.requestRender());
    }

    setComposition(comp: VideoComposition) {
        this.comp = comp;
        this.media.sync(comp);
        if (this.playing) this.syncMedia(this.time, true);
        else this.parkMedia(this.time);
        this.requestRender();
    }

    get duration(): number {
        return this.comp ? totalDuration(this.comp) : 0;
    }

    play() {
        if (!this.comp || this.playing) return;
        if (this.time >= this.duration - 1e-3) this.time = 0;
        this.playing = true;
        this.wallStart = performance.now();
        this.timeStart = this.time;
        this.syncMedia(this.time, true);
        this.onPlayingChange?.(true);
        const tick = () => {
            if (!this.playing) return;
            const t = this.timeStart + (performance.now() - this.wallStart) / 1000;
            const end = this.duration;
            if (t >= end) {
                this.time = end;
                this.pause();
                this.onTime?.(end);
                return;
            }
            this.time = t;
            this.syncMedia(t, false);
            this.draw();
            this.onTime?.(t);
            this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }

    pause() {
        if (!this.playing) return;
        this.playing = false;
        cancelAnimationFrame(this.raf);
        this.media.allVideos().forEach((el) => el.pause());
        this.media.allAudios().forEach((el) => el.pause());
        this.parkMedia(this.time);
        this.onPlayingChange?.(false);
        this.requestRender();
    }

    toggle() {
        if (this.playing) this.pause();
        else this.play();
    }

    seek(t: number) {
        const clamped = Math.max(0, Math.min(t, this.duration));
        this.time = clamped;
        if (this.playing) {
            this.wallStart = performance.now();
            this.timeStart = clamped;
            this.syncMedia(clamped, true);
        } else {
            this.parkMedia(clamped);
        }
        this.requestRender();
        this.onTime?.(clamped);
    }

    requestRender() {
        if (this.renderQueued || this.playing) return;
        this.renderQueued = requestAnimationFrame(() => {
            this.renderQueued = 0;
            this.draw();
        });
    }

    draw() {
        if (!this.comp) return;
        renderFrame(this.ctx, this.comp, this.time, this.media, { hitBoxes: this.hitBoxes });
        this.onDraw?.();
    }

    dispose() {
        this.playing = false;
        cancelAnimationFrame(this.raf);
        cancelAnimationFrame(this.renderQueued);
        this.unsub();
    }

    // ─── media sync ─────────────────────────────────────────────────

    /** Clips whose picture or sound matters at t (current, outgoing, about to start). */
    private relevantClips(t: number): MainClip[] {
        const clips = this.comp ? mainTrack(this.comp).clips : [];
        return clips.filter((c) => t >= c.start - PREROLL && t < itemEnd(c));
    }

    /** Paused: show the exact frame at t. */
    private parkMedia(t: number) {
        if (!this.comp) return;
        const relevant = new Set(this.relevantClips(t).map((c) => c.id));
        for (const c of mainTrack(this.comp).clips) {
            if (c.kind !== 'video') continue;
            const el = this.media.peekVideo(c.id);
            if (!el) continue;
            if (!el.paused) el.pause();
            if (relevant.has(c.id)) {
                const target = sourceTimeAt(c, t);
                if (Math.abs(el.currentTime - target) > 0.01) el.currentTime = target;
            }
        }
    }

    private syncMedia(t: number, hard: boolean) {
        if (!this.comp) return;
        const comp = this.comp;
        const clips = mainTrack(comp).clips;
        const state = mainFrameState(clips, t);
        const active = new Set<string>();
        if (state.current) active.add(state.current.id);
        if (state.previous) active.add(state.previous.id);
        for (const c of clips) {
            if (c.kind !== 'video') continue;
            const el = this.media.video(c);
            const isActive = active.has(c.id);
            const upcoming = !isActive && t >= c.start - PREROLL && t < c.start;
            if (isActive) {
                const target = sourceTimeAt(c, t);
                if (hard || Math.abs(el.currentTime - target) > DRIFT) el.currentTime = target;
                // Slow motion / speed-up. The export shifts pitch with the rate
                // (OfflineAudioContext playbackRate), so the preview does too.
                const rate = clipSpeed(c);
                if (el.playbackRate !== rate) el.playbackRate = rate;
                if ('preservesPitch' in el) (el as any).preservesPitch = rate === 1;
                el.volume = clipVolume(c, t, state.current?.id === c.id ? state.transitionProgress : 1 - state.transitionProgress);
                el.muted = c.muted || c.volume <= 0;
                if (el.paused) void el.play().catch(() => undefined);
            } else {
                if (!el.paused) el.pause();
                if (upcoming && Math.abs(el.currentTime - c.trimIn) > 0.05) el.currentTime = c.trimIn;
            }
        }
        for (const track of audioTracks(comp)) {
            for (const a of track.clips) {
                const el = this.media.audio(a);
                const on = !track.muted && t >= a.start && t < itemEnd(a);
                if (!on) {
                    if (!el.paused) el.pause();
                    continue;
                }
                const target = a.trimIn + (t - a.start);
                if (hard || Math.abs(el.currentTime - target) > DRIFT) el.currentTime = target;
                el.volume = clamp01(a.volume * fadeGain(t - a.start, a.duration, a.fadeIn, a.fadeOut));
                if (el.paused) void el.play().catch(() => undefined);
            }
        }
    }
}

function clipVolume(c: MainClip, t: number, transitionShare: number): number {
    if (c.muted) return 0;
    return clamp01(c.volume * clamp01(transitionShare));
}

/** Gain 0..1 at local time `lt` for a clip of `dur` with fades. */
export function fadeGain(lt: number, dur: number, fadeIn: number, fadeOut: number): number {
    let g = 1;
    if (fadeIn > 0 && lt < fadeIn) g *= clamp01(lt / fadeIn);
    if (fadeOut > 0 && lt > dur - fadeOut) g *= clamp01((dur - lt) / fadeOut);
    return g;
}
